import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { or } from 'ember-truth-helpers';
import { t } from 'ember-intl';
import type Session from '../services/session';
import type IntlService from 'ember-intl/services/intl';
import type Notifications from '../services/notifications';
import type RouterService from '@ember/routing/router-service';
import Icon from './icon';
import PlatformBadge from './platform-badge';
import AlertCircleIcon from '~icons/lucide/alert-circle';
import RefreshCwIcon from '~icons/lucide/refresh-cw';
import imageFadeIn from '../modifiers/image-fade-in';
import { apiFetch, toApiUrl } from '../fetch';
import { formatDuration, formatDurationHuman } from '../utils/format';
import {
  type JobState,
  type TimingStep,
  type StepTimings,
  type SseEventData,
  type PipelineStepId,
  type StepStatus,
  type StepEvent,
  type SnapshotEvent,
  type SourcePatch,
  PIPELINE_STEPS,
  stepOrderForRoute,
  isInProgressState,
  isTerminalState,
  stepCssClass,
  summaryLabel,
} from '../utils/pipeline-types';
import {
  STEP_TR_KEYS,
  STEP_DESCRIPTION_TR_KEYS,
  FINE_STEP_TR_KEYS,
} from '../utils/tr-keys';
import type { ContentSource } from '../schemas/content-source.ts';

interface Signature {
  Args: {
    source: ContentSource;
    onCompleted?: () => void;
    onRefresh?: () => unknown;
    onRetry?: () => void;
  };
}

/** Accumulated view state for one fine-grained pipeline step */
interface StepRecord {
  status: StepStatus;
  summary?: Record<string, unknown>;
  error?: string;
  startedAt?: number;
  completedAt?: number;
}

/** Row rendered in the fine-grained timeline */
interface FineStepView {
  id: PipelineStepId;
  css: string;
  trKey: string;
  duration: string | null;
  summaryLabel: string | null;
}

export default class SourceProcessing extends Component<Signature> {
  @service declare session: Session;
  @service declare intl: IntlService;
  @service declare notifications: Notifications;
  @service declare router: RouterService;

  @tracked state: JobState;
  @tracked error: string | null = null;
  @tracked stepTimings: StepTimings | null = null;
  @tracked elapsedClockMs = Date.now();
  @tracked isRetrying = false;
  @tracked clientStepDurations = new Map<string, number>();

  // SSE-delivered metadata (updates as pipeline progresses)
  @tracked sseTitle: string | null = null;
  @tracked sseThumbnailUrl: string | null = null;
  @tracked sseDescription: string | null = null;
  @tracked sseDuration: number | null = null;
  @tracked sseLanguage: string | null = null;

  // Fine-grained step events (SSE v2 snapshot/step events)
  @tracked stepEvents = new Map<PipelineStepId, StepRecord>();
  @tracked progressPct: number | null = null;
  private currentRunId: string | null = null;

  private stepStartTimes = new Map<string, number>();
  private eventSource: EventSource | null = null;
  private elapsedTimer: ReturnType<typeof setInterval> | null = null;

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args);
    const sourceState =
      (this.args.source.state as JobState | null) ?? 'pending';
    this.state = sourceState;

    if (isInProgressState(sourceState)) {
      const updatedAt = this.args.source.updatedAt;
      this.stepStartTimes.set(
        sourceState,
        updatedAt ? new Date(updatedAt).getTime() : Date.now()
      );
    }

    if (!isTerminalState(sourceState)) {
      this.startElapsedClock();
      this.startStatusPolling();
    }
  }

  // ── Display getters (SSE overrides args) ──────────────────────────

  get displayTitle(): string | null {
    return this.sseTitle ?? this.args.source.title ?? null;
  }

  get displayThumbnailUrl(): string | null {
    return this.sseThumbnailUrl ?? this.args.source.thumbnailUrl ?? null;
  }

  get displayDescription(): string | null {
    return this.sseDescription ?? this.args.source.description ?? null;
  }

  get displayDuration(): number | null {
    return this.sseDuration ?? this.args.source.duration ?? null;
  }

  get displayLanguage(): string | null {
    return this.sseLanguage ?? this.args.source.language ?? null;
  }

  // ── State getters ─────────────────────────────────────────────────

  get sourceId(): string {
    return this.args.source.id ?? '';
  }

  get isInProgress(): boolean {
    return isInProgressState(this.state);
  }

  get isTerminal(): boolean {
    return isTerminalState(this.state);
  }

  get isCompleted(): boolean {
    return this.state === 'completed';
  }

  get isFailed(): boolean {
    return this.state === 'failed' || this.state === 'not_found';
  }

  get isAdmin(): boolean {
    return this.session.isAdmin || this.session.isModerator;
  }

  get stepDescription(): string {
    const key = STEP_DESCRIPTION_TR_KEYS[this.state];
    if (!key) return '';
    return this.intl.t(key);
  }

  get totalDuration(): string | null {
    if (this.stepTimings?.total != null) {
      return formatDurationHuman(this.stepTimings.total);
    }
    // Use updatedAt as the baseline: resetJobs (retry/reprocess) stamps updatedAt = now,
    // so the timer resets to 0 after a retry rather than counting from original creation.
    const baseline = this.args.source.updatedAt ?? this.args.source.createdAt;
    if (!baseline) return null;
    void this.elapsedClockMs;
    return formatDurationHuman(
      Math.max(0, Date.now() - new Date(baseline).getTime())
    );
  }

  // ── Pipeline step view models (data-driven) ──────────────────────

  get pipelineSteps(): {
    key: JobState;
    css: string;
    duration: string | null;
    trKey: string;
  }[] {
    return PIPELINE_STEPS.map((key) => ({
      key,
      css: stepCssClass(this.state, key),
      duration: this.getStepDuration(key as TimingStep),
      trKey: STEP_TR_KEYS[key] ?? key,
    }));
  }

  /** Whether fine-grained step events arrived for this run */
  get showFineTimeline(): boolean {
    return this.stepEvents.size > 0;
  }

  get fineSteps(): FineStepView[] {
    return stepOrderForRoute(this.stepEvents.keys())
      .filter((id) => this.stepEvents.get(id)?.status !== 'skipped')
      .map((id) => {
        const record = this.stepEvents.get(id);
        return {
          id,
          css: this.fineStepCss(record),
          trKey: FINE_STEP_TR_KEYS[id] ?? id,
          duration: this.fineStepDuration(record),
          summaryLabel: record ? summaryLabel(id, record.summary) : null,
        };
      });
  }

  private fineStepCss(record: StepRecord | undefined): string {
    if (!record) return '';
    if (record.status === 'failed') return 'step-error';
    return 'step-primary';
  }

  private fineStepDuration(record: StepRecord | undefined): string | null {
    if (!record?.startedAt) return null;
    if (record.completedAt) {
      const ms = record.completedAt - record.startedAt;
      return ms >= 500 ? formatDurationHuman(ms) : null;
    }
    if (record.status === 'started') {
      void this.elapsedClockMs;
      return formatDurationHuman(Math.max(0, Date.now() - record.startedAt));
    }
    return null;
  }

  // ── Actions ───────────────────────────────────────────────────────

  @action
  retry(): void {
    this.isRetrying = true;
    const authHeader = this.session.authHeader;
    if (!authHeader) {
      this.isRetrying = false;
      return;
    }

    void apiFetch(`/api/admin/sources/${this.sourceId}/reprocess`, {
      method: 'POST',
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/vnd.api+json',
      },
    })
      .then((response) => {
        if (response.ok) {
          this.args.onRetry?.();
        } else {
          this.error = 'Failed to retry extraction';
          this.isRetrying = false;
        }
      })
      .catch(() => {
        this.error = 'Failed to retry extraction';
        this.isRetrying = false;
      });
  }

  // ── Private helpers ───────────────────────────────────────────────

  private getStepDuration(step: TimingStep | JobState): string | null {
    if (step === 'pending' || step === 'completed') return null;
    const timingStep = step as TimingStep;
    if (this.stepTimings) {
      const ms = this.stepTimings[timingStep];
      return ms != null && ms > 0 ? formatDurationHuman(ms) : null;
    }
    const clientMs = this.clientStepDurations.get(timingStep);
    if (clientMs != null) return formatDurationHuman(clientMs);
    if (timingStep === this.state) {
      const start = this.stepStartTimes.get(timingStep);
      if (start) {
        void this.elapsedClockMs;
        return formatDurationHuman(Math.max(0, Date.now() - start));
      }
    }
    return null;
  }

  private startStatusPolling(): void {
    this.eventSource?.close();
    this.eventSource = new EventSource(
      toApiUrl(
        `/api/submissions/status?id=${encodeURIComponent(this.sourceId)}`
      )
    );
    this.eventSource.addEventListener('status', (event) => {
      try {
        const data = JSON.parse(String(event.data)) as SseEventData;
        this.handleStatusEvent(data);
      } catch (err) {
        console.error('Failed to parse SSE data', err);
      }
    });
    this.eventSource.addEventListener('snapshot', (event) => {
      try {
        this.handleSnapshot(JSON.parse(String(event.data)) as SnapshotEvent);
      } catch (err) {
        console.error('Failed to parse SSE snapshot', err);
      }
    });
    this.eventSource.addEventListener('step', (event) => {
      try {
        this.applyStepEvent(JSON.parse(String(event.data)) as StepEvent);
      } catch (err) {
        console.error('Failed to parse SSE step event', err);
      }
    });
    this.eventSource.addEventListener('source', (event) => {
      try {
        this.applySourcePatch(JSON.parse(String(event.data)) as SourcePatch);
      } catch (err) {
        console.error('Failed to parse SSE source patch', err);
      }
    });
    this.eventSource.onerror = () => {
      this.eventSource?.close();
      if (!isTerminalState(this.state)) {
        this.state = 'failed';
        this.error = 'Connection to server lost';
        this.stopElapsedClock();
      }
    };
  }

  /** Seed state from the SSE snapshot (source + persisted step events). */
  private handleSnapshot(snapshot: SnapshotEvent): void {
    this.updateMetadata(snapshot.source);
    if (snapshot.source.state && snapshot.source.state !== this.state) {
      const prev = this.state;
      this.state = snapshot.source.state;
      this.trackStepTransition(prev, this.state);
    }
    this.currentRunId = snapshot.runId;
    for (const event of snapshot.steps) {
      this.applyStepEvent(event);
    }
  }

  /** Merge one fine-grained step event into the timeline state. */
  private applyStepEvent(event: StepEvent): void {
    // A new run (retry/reprocess) supersedes the previous run's timeline
    if (this.currentRunId && event.runId !== this.currentRunId) {
      this.stepEvents = new Map();
      this.progressPct = null;
    }
    this.currentRunId = event.runId;

    const existing = this.stepEvents.get(event.stepId);
    // Late replays: never let 'started' overwrite a terminal step status
    if (
      existing &&
      (existing.status === 'completed' || existing.status === 'failed') &&
      event.status === 'started'
    ) {
      return;
    }

    const next = new Map(this.stepEvents);
    next.set(event.stepId, {
      status: event.status,
      summary: event.summary ?? existing?.summary,
      error: event.error,
      startedAt:
        event.status === 'started' ? event.timestamp : existing?.startedAt,
      completedAt:
        event.status === 'completed' || event.status === 'failed'
          ? event.timestamp
          : existing?.completedAt,
    });
    this.stepEvents = next;

    if (typeof event.progress === 'number') {
      this.progressPct = Math.max(this.progressPct ?? 0, event.progress);
    }
    // Coarse state piggybacks on 'started' events (downloading/transcribing/…)
    if (event.state && !isTerminalState(this.state)) {
      this.state = event.state;
    }
    if (event.sourcePatch) this.applySourcePatch(event.sourcePatch);
  }

  /** Early metadata (title/thumbnail/…) delivered before extraction finishes. */
  private applySourcePatch(patch: SourcePatch): void {
    if (patch.title) this.sseTitle = patch.title;
    if (patch.thumbnailUrl) this.sseThumbnailUrl = patch.thumbnailUrl;
    if (patch.description) this.sseDescription = patch.description;
    if (patch.duration != null) this.sseDuration = patch.duration;
    if (patch.language) this.sseLanguage = patch.language;
  }

  private handleStatusEvent(data: SseEventData): void {
    const prevState = this.state;
    this.state = data.state;
    this.updateMetadata(data);
    this.trackStepTransition(prevState, data.state);

    if (data.errorMessage) this.error = data.errorMessage;
    else if (data.error) this.error = data.error;

    if (isTerminalState(this.state)) {
      this.stopElapsedClock();
      this.eventSource?.close();
      if (this.state === 'completed') {
        this.notifications.success('Extraction Complete!', {
          description: 'Results have been loaded successfully.',
        });
        setTimeout(() => {
          this.args.onCompleted?.();
          // Use forced-refresh if available (sets reload:true on WarpDrive request)
          // so stale cached state is not returned after extraction completes.
          if (this.args.onRefresh) {
            void this.args.onRefresh();
          } else {
            void this.router.refresh();
          }
        }, 500);
      }
    }
  }

  private updateMetadata(data: SseEventData): void {
    if (data.title) this.sseTitle = data.title;
    if (data.thumbnailUrl) this.sseThumbnailUrl = data.thumbnailUrl;
    if (data.description) this.sseDescription = data.description;
    if (data.duration != null) this.sseDuration = data.duration;
    if (data.language) this.sseLanguage = data.language;
    if (data.stepTimings) this.stepTimings = data.stepTimings;
  }

  private trackStepTransition(prev: JobState, next: JobState): void {
    if (prev === next) return;
    const now = Date.now();
    const prevStart = this.stepStartTimes.get(prev);
    if (prevStart && isInProgressState(prev)) {
      const durations = new Map(this.clientStepDurations);
      durations.set(prev, now - prevStart);
      this.clientStepDurations = durations;
    }
    if (isInProgressState(next)) {
      this.stepStartTimes.set(next, now);
    }
  }

  private startElapsedClock(): void {
    if (this.elapsedTimer) clearInterval(this.elapsedTimer);
    this.elapsedClockMs = Date.now();
    this.elapsedTimer = setInterval(() => {
      this.elapsedClockMs = Date.now();
    }, 1000);
  }

  private stopElapsedClock(): void {
    if (!this.elapsedTimer) return;
    clearInterval(this.elapsedTimer);
    this.elapsedTimer = null;
  }

  willDestroy(): void {
    super.willDestroy();
    this.eventSource?.close();
    this.stopElapsedClock();
  }

  <template>
    <div class="bg-base-100 rounded-box p-6 shadow" data-test-source-processing>
      {{! Thumbnail banner — skeleton until download delivers the URL }}
      <figure class="rounded-t-box -mx-6 -mt-6 mb-4 h-56 overflow-hidden">
        {{#if this.displayThumbnailUrl}}
          <img
            {{imageFadeIn}}
            src={{this.displayThumbnailUrl}}
            alt={{or this.displayTitle "Source thumbnail"}}
            class="h-full w-full object-cover opacity-0 transition-opacity duration-300"
            loading="lazy"
          />
        {{else}}
          <div class="bg-base-300 skeleton h-full w-full rounded-none"></div>
        {{/if}}
      </figure>

      {{! Title — skeleton until metadata arrives }}
      {{#if this.displayTitle}}
        <h1 class="mb-2 text-2xl font-bold">{{this.displayTitle}}</h1>
      {{else}}
        <div class="skeleton mb-2 h-8 w-3/4"></div>
      {{/if}}

      {{! Badges row: platform, language, duration }}
      <div class="mb-4 flex flex-wrap items-center gap-3">
        <PlatformBadge @platform={{@source.platform}} />
        {{#if this.displayLanguage}}
          <span class="badge badge-outline">{{this.displayLanguage}}</span>
        {{else}}
          <div class="skeleton h-5 w-10"></div>
        {{/if}}
        {{#if this.displayDuration}}
          <span class="text-base-content/60 text-sm">
            {{formatDuration this.displayDuration}}
          </span>
        {{else}}
          <div class="skeleton h-4 w-12"></div>
        {{/if}}
      </div>

      {{! Link to original source — hide for image:// uploads }}
      {{#if (isExternalUrl @source.url)}}
        <a
          href={{@source.url}}
          target="_blank"
          rel="noopener noreferrer"
          class="link link-primary"
        >
          View original &nearr;
        </a>
      {{/if}}

      {{! Overall progress bar (fed by fine-grained step events) }}
      {{#if this.progressPct}}
        {{#if this.isInProgress}}
          <progress
            class="progress progress-primary mt-6 w-full"
            value={{this.progressPct}}
            max="100"
            data-test-pipeline-progress
          ></progress>
        {{/if}}
      {{/if}}

      {{! Steps timeline: fine-grained when step events stream in,
          coarse state-based fallback otherwise (image/photo sources) }}
      {{#if this.showFineTimeline}}
        <ul
          class="steps steps-vertical lg:steps-horizontal mt-4 mb-2 w-full"
          data-test-fine-timeline
        >
          {{#each this.fineSteps as |step|}}
            <li class="step {{step.css}}" data-test-fine-step={{step.id}}>
              <span class="flex flex-col items-center gap-0.5">
                <span>
                  {{t step.trKey}}
                  {{#if step.duration}}({{step.duration}}){{/if}}
                </span>
                {{#if step.summaryLabel}}
                  <span class="text-base-content/50 text-xs">
                    {{step.summaryLabel}}
                  </span>
                {{/if}}
              </span>
            </li>
          {{/each}}
        </ul>
      {{else}}
        <ul class="steps steps-vertical lg:steps-horizontal mt-6 mb-2 w-full">
          {{#each this.pipelineSteps as |step|}}
            <li class="step {{step.css}}">
              {{t step.trKey}}
              {{#if step.duration}}
                ({{step.duration}})
              {{/if}}
            </li>
          {{/each}}
        </ul>
      {{/if}}

      {{! Current step description with spinner and total elapsed }}
      {{#if this.stepDescription}}
        <p
          class="text-base-content/55 mb-4 text-sm italic"
          data-test-processing-step
        >
          {{#if this.isInProgress}}
            <span class="loading loading-spinner loading-xs mr-1"></span>
          {{/if}}
          {{this.stepDescription}}
          {{#if this.totalDuration}}
            <span class="text-base-content/40 ml-2 font-mono text-xs">
              {{this.totalDuration}}
            </span>
          {{/if}}
        </p>
      {{/if}}

      {{! Description — skeleton until metadata arrives }}
      {{#if this.displayDescription}}
        <p class="text-base-content/80 mt-4">{{this.displayDescription}}</p>
      {{else if this.isInProgress}}
        <div class="mt-4 space-y-2">
          <div class="skeleton h-4 w-full"></div>
          <div class="skeleton h-4 w-5/6"></div>
        </div>
      {{/if}}

      {{! AI Summary placeholder — skeleton during processing }}
      {{#if this.isInProgress}}
        <div class="bg-base-200 rounded-box mt-4 p-4">
          <h3
            class="mb-2 text-sm font-semibold tracking-wide uppercase opacity-60"
          >
            AI Summary
          </h3>
          <div class="space-y-2">
            <div class="skeleton h-4 w-full"></div>
            <div class="skeleton h-4 w-4/5"></div>
            <div class="skeleton h-4 w-3/5"></div>
          </div>
        </div>
      {{/if}}

      {{! Mentions placeholder — skeleton during processing }}
      {{#if this.isInProgress}}
        <h2 class="mt-8 mb-4 text-2xl font-bold opacity-40">
          Extracted Mentions
        </h2>
        <div class="flex flex-col gap-4">
          <div class="skeleton rounded-box h-24 w-full"></div>
          <div class="skeleton rounded-box h-24 w-full"></div>
          <div class="skeleton rounded-box h-24 w-full"></div>
        </div>
      {{/if}}

      {{! Failure alert }}
      {{#if this.isFailed}}
        <div class="alert alert-error mt-6">
          <Icon @icon={{AlertCircleIcon}} class="h-6 w-6" />
          <div>
            <h3 class="font-bold">Extraction Failed</h3>
            <div class="text-sm">
              {{or this.error "An unknown error occurred during extraction."}}
            </div>
          </div>
          {{#if this.isAdmin}}
            <button
              type="button"
              class="btn btn-sm btn-outline"
              data-test-retry-extraction
              disabled={{this.isRetrying}}
              {{on "click" this.retry}}
            >
              {{#if this.isRetrying}}
                <span class="loading loading-spinner loading-sm"></span>
                Retrying…
              {{else}}
                <Icon @icon={{RefreshCwIcon}} class="h-4 w-4" />
                Retry
              {{/if}}
            </button>
          {{/if}}
        </div>
      {{/if}}
    </div>
  </template>
}

/**
 * Returns true only for real external URLs; hides the link for
 * image:// upload identifiers (no public URL to link to).
 */
function isExternalUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  return !url.startsWith('image://');
}
